import { Toaster } from "@/components/ui/toaster";
import React, { Suspense, lazy } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { ViewLimitProvider } from "@/gating/ViewLimitProvider";
import { PricingModalProvider } from "@/context/PricingModalContext";
import { PricingModal } from "@/components/PricingModal";
import { usePricingModal } from "@/context/PricingModalContext";
import { isRecoveryFlow } from "@/utils/isRecoveryFlow";
import { useEntitlements } from "@/entitlements/useEntitlements";
import Library from "./pages/Library";
import Favorites from "./pages/Favorites";
import Auth from "./pages/Auth";
import SignupPage from "./pages/auth/SignupPage";
import SigninPage from "./pages/auth/SigninPage";
import AdDetail from "./pages/AdDetail";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import { useEffect, useState } from "react";
import { supabase } from "./integrations/supabase/client";
import { AppShell } from "@/components/sidebar/AppShell";
import { SidebarProvider } from "@/components/ui/sidebar";
import Header from "./components/Header";
import ObyectLoader from "@/components/ObyectLoader";

// Lazy-loaded admin pages
const AdminUpload = lazy(() => import("./pages/AdminUpload"));
const AdminAnalyticsPage = lazy(() => import("./components/admin/Analytics"));
const AdminRequestsPage = lazy(() => import("./pages/admin/AdminRequests"));

// Lazy-loaded auth pages
const ForgotPasswordPage = lazy(() => import("./pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/auth/ResetPasswordPage"));

// Lazy-loaded request pages
const RequestsPage = lazy(() => import("./pages/Requests"));
const NewRequestPage = lazy(() => import("./pages/NewRequest"));
const RequestDetailPage = lazy(() => import("./pages/RequestDetail"));

// Lazy-loaded briefing pages
const BriefingsPage = lazy(() => import("./pages/Briefings"));
const BriefingWizard = lazy(() => import("./pages/BriefingWizard"));
const BriefingEditor = lazy(() => import("./pages/BriefingEditor"));
const BriefingShare = lazy(() => import("./pages/BriefingShare"));

// Lazy-loaded seasonal/specialty pages
const BlackFriday = lazy(() => import("./pages/BlackFriday"));
const NewYearsBoard = lazy(() => import("./pages/NewYearsBoard"));

// Lazy-loaded main pages
const TopPicks = lazy(() => import("./pages/TopPicks"));
const BrandsPage = lazy(() => import("./pages/Brands"));

// Lazy-loaded profile and legal pages
const Profile = lazy(() => import("./pages/Profile"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const BrandsSettingsPage = lazy(() => import("./pages/settings/Brands"));

// Lazy-loaded collection and error pages
const CollectionPage = lazy(() => import("./pages/CollectionPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();
/** 0) If URL indicates recovery and we’re not on /auth/reset-password, push there immediately */
function RecoveryRouter() {
  const navigate = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (isRecoveryFlow() && !loc.pathname.startsWith("/auth/reset-password")) {
      const suffix = location.search + location.hash; // keep tokens
      navigate(`/auth/reset-password${suffix}`, { replace: true });
    }
  }, [loc.pathname, navigate]);

  return null;
}

/** 1) When Supabase emits PASSWORD_RECOVERY, force the reset page (safety net) */
function RecoveryListener() {
  const navigate = useNavigate();
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        const suffix = location.search + location.hash;
        navigate(`/auth/reset-password${suffix}`, { replace: true });
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);
  return null;
}

/** 2) Your normal “if session ⇒ /library” redirect — but skip it during recovery */
function SessionRedirect() {
  const navigate = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) return;

      // Hard stop: NEVER redirect the reset page
      if (loc.pathname.startsWith("/auth/reset-password")) return;

      // Skip any recovery-ish URL (hash/search)
      if (isRecoveryFlow()) return;

      if (loc.pathname.startsWith("/auth")) {
        navigate("/library", { replace: true });
      }
    })();
  }, [loc.pathname, navigate]);

  return null;
}
function RootRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    const suffix = location.search + location.hash;
    if (isRecoveryFlow()) {
      navigate(`/auth/reset-password${suffix}`, { replace: true });
    } else {
      navigate("/library", { replace: true });
    }
  }, [navigate]);
  return null;
}

const AppRoutes = () => {
  const location = useLocation();
  const state = location.state as { backgroundLocation?: Location };
  const { isOpen, closePricingModal } = usePricingModal();
  const { isAuthenticated } = useEntitlements();
  const isAuthRoute = location.pathname.startsWith("/auth");
  const showSidebar = !isAuthRoute;

   // header timeframe state (for guest header)
  type TF = "week" | "month" | "year" | "all";
  const [headerTimeframe, setHeaderTimeframe] = useState<TF>("all");

  return (
    <>
      <RecoveryRouter />
      <RecoveryListener />
      <SessionRedirect />

    {showSidebar ? (
      <AppShell>
        {/* The AppShell already defines layout and scroll */}
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><ObyectLoader size={64} /></div>}>
          <Routes location={state?.backgroundLocation || location}>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/library" element={<Library />} />
            <Route path="/brands" element={<BrandsPage />} />
            <Route path="/black-friday" element={<Suspense fallback={<ObyectLoader />}><BlackFriday /></Suspense>} />
            <Route path="/top-picks" element={<TopPicks />} />
            <Route path="/new-years" element={<Suspense fallback={<ObyectLoader />}><NewYearsBoard /></Suspense>} />
            <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
            <Route path="/admin" element={<AdminRoute><Suspense fallback={<ObyectLoader />}><AdminUpload /></Suspense></AdminRoute>} />
            <Route path="/admin/analytics" element={<AdminRoute><Suspense fallback={<ObyectLoader />}><AdminAnalyticsPage /></Suspense></AdminRoute>} />
            <Route path="/admin/requests" element={<AdminRoute><Suspense fallback={<ObyectLoader />}><AdminRequestsPage /></Suspense></AdminRoute>} />
            <Route path="/requests" element={<ProtectedRoute><Suspense fallback={<ObyectLoader />}><RequestsPage /></Suspense></ProtectedRoute>} />
            <Route path="/requests/new" element={<ProtectedRoute><Suspense fallback={<ObyectLoader />}><NewRequestPage /></Suspense></ProtectedRoute>} />
            <Route path="/requests/:id" element={<ProtectedRoute><Suspense fallback={<ObyectLoader />}><RequestDetailPage /></Suspense></ProtectedRoute>} />
            <Route path="/settings/brands" element={<ProtectedRoute><Suspense fallback={<ObyectLoader />}><BrandsSettingsPage /></Suspense></ProtectedRoute>} />
            <Route path="/briefings" element={<ProtectedRoute><Suspense fallback={<ObyectLoader />}><BriefingsPage /></Suspense></ProtectedRoute>} />
            <Route path="/briefings/new" element={<ProtectedRoute><Suspense fallback={<ObyectLoader />}><BriefingWizard /></Suspense></ProtectedRoute>} />
            <Route path="/briefings/:id" element={<ProtectedRoute><Suspense fallback={<ObyectLoader />}><BriefingEditor /></Suspense></ProtectedRoute>} />
            <Route path="/share/:token" element={<Suspense fallback={<ObyectLoader />}><BriefingShare /></Suspense>} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/signup" element={<SignupPage />} />
            <Route path="/auth/signin" element={<SigninPage />} />
            <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
            <Route path="/profile" element={<ProtectedRoute><Suspense fallback={<ObyectLoader />}><Profile /></Suspense></ProtectedRoute>} />
            <Route path="/terms" element={<Suspense fallback={<ObyectLoader />}><Terms /></Suspense>} />
            <Route path="/privacy" element={<Suspense fallback={<ObyectLoader />}><Privacy /></Suspense>} />
            <Route path="/ads/:id" element={<AdDetail />} />
            <Route path="/collection/:id" element={<Suspense fallback={<ObyectLoader />}><CollectionPage /></Suspense>} />
            <Route path="*" element={<Suspense fallback={<ObyectLoader />}><NotFound /></Suspense>} />
          </Routes>

          {state?.backgroundLocation && (
            <Routes>
              <Route path="/ads/:id" element={<AdDetail modal />} />
            </Routes>
          )}
        </Suspense>
      </AppShell>
      ) : (
        // No sidebar version: keep it simple and let the page be responsive like logged-out
        <div className="min-h-[100dvh] bg-background">
          {/* <Header /> */}
          <div className="pt-16">
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><ObyectLoader size={64} /></div>}>
              <Routes location={state?.backgroundLocation || location}>
                <Route path="/" element={<RootRedirect />} />
                <Route path="/library" element={<Library />} />
                <Route path="/brands" element={<BrandsPage />} />
                <Route path="/black-friday" element={<Suspense fallback={<ObyectLoader />}><BlackFriday /></Suspense>} />
                <Route path="/top-picks" element={<TopPicks />} />
                <Route path="/new-years" element={<Suspense fallback={<ObyectLoader />}><NewYearsBoard /></Suspense>} />
                <Route
                  path="/favorites"
                  element={
                    <ProtectedRoute>
                      <Favorites />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <AdminRoute>
                      <Suspense fallback={<ObyectLoader />}>
                        <AdminUpload />
                      </Suspense>
                    </AdminRoute>
                  }
                />
                <Route path="/auth" element={<Auth />} />
                <Route path="/auth/signup" element={<SignupPage />} />
                <Route path="/auth/signin" element={<SigninPage />} />
                <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<ObyectLoader />}>
                        <Profile />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route path="/terms" element={<Suspense fallback={<ObyectLoader />}><Terms /></Suspense>} />
                <Route path="/privacy" element={<Suspense fallback={<ObyectLoader />}><Privacy /></Suspense>} />
                <Route path="/ads/:id" element={<AdDetail />} />
                <Route path="/collection/:id" element={<Suspense fallback={<ObyectLoader />}><CollectionPage /></Suspense>} />
                <Route path="*" element={<Suspense fallback={<ObyectLoader />}><NotFound /></Suspense>} />
              </Routes>

              {state?.backgroundLocation && (
                <Routes>
                  <Route path="/ads/:id" element={<AdDetail modal />} />
                </Routes>
              )}
            </Suspense>
          </div>
        </div>
      )}

      <PricingModal open={isOpen} onOpenChange={closePricingModal} />
    </>
  );
};

import { GlobalBackground } from "./components/layout/GlobalBackground";

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <GlobalBackground />
        <PricingModalProvider>
          <ViewLimitProvider>
            <AppRoutes />
          </ViewLimitProvider>
        </PricingModalProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
